import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import toast from 'react-hot-toast';
import api from '../utils/api';

interface LottoHistoryEntry {
  id: string;
  lottoId: string;
  date: Date;
  status: 'PENDING' | 'WON' | 'LOST';
  fairnessPointsUsed: number;
  createdAt: Date;
}

interface LottoHistoryStats {
  total: number;
  won: number;
  lost: number;
  pending: number;
  winRate: number;
}

const LottoHistoryPage: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<LottoHistoryStats | null>(null);
  const [entries, setEntries] = useState<LottoHistoryEntry[]>([]);
  const [byMonth, setByMonth] = useState<{ [key: string]: LottoHistoryEntry[] }>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/lotto/history');
      setStats(response.data.data.stats);
      setEntries(response.data.data.entries);
      setByMonth(response.data.data.byMonth);
    } catch (error: any) {
      console.error('히스토리 조회 실패:', error);
      toast.error(error.response?.data?.message || '히스토리 조회 실패');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <main className="container mx-auto px-4 py-6 pb-0">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <div className="text-4xl mb-4">⏳</div>
            <p className="text-gray-600">로딩 중...</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="container mx-auto px-4 py-6 pb-0">
      <div className="max-w-4xl mx-auto">
        {/* 헤더 */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-primary mb-2">
            🎲 추첨 히스토리
          </h1>
          <p className="text-sm text-gray-600">
            나의 추첨 기록과 통계를 확인하세요
          </p>
        </div>

        {/* 통계 카드 */}
        {stats && (
          <div className="space-y-3 mb-6">
            {/* 상단 2x2 그리드 */}
            <div className="grid grid-cols-2 gap-3">
              {/* 총 참여 */}
              <div className="card-azocsii p-4 text-center">
                <div className="text-xs text-gray-600 mb-1">총 참여</div>
                <div className="text-2xl font-bold text-primary">{stats.total}</div>
                <div className="text-xs text-gray-500">회</div>
              </div>

              {/* 당첨 */}
              <div className="card-azocsii p-4 text-center bg-green-50">
                <div className="text-xs text-gray-600 mb-1">🎉 당첨</div>
                <div className="text-2xl font-bold text-green-600">{stats.won}</div>
                <div className="text-xs text-gray-500">회</div>
              </div>

              {/* 탈락 */}
              <div className="card-azocsii p-4 text-center bg-red-50">
                <div className="text-xs text-gray-600 mb-1">😢 탈락</div>
                <div className="text-2xl font-bold text-red-600">{stats.lost}</div>
                <div className="text-xs text-gray-500">회</div>
              </div>

              {/* 대기 중 */}
              <div className="card-azocsii p-4 text-center bg-yellow-50">
                <div className="text-xs text-gray-600 mb-1">⏳ 대기</div>
                <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
                <div className="text-xs text-gray-500">회</div>
              </div>
            </div>

            {/* 당첨률 - 가로 길게 */}
            <div className="card-azocsii p-4 bg-purple-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">📊</span>
                  <div>
                    <div className="text-xs text-gray-600">당첨률</div>
                    <div className="text-sm text-gray-500">전체 평균</div>
                  </div>
                </div>
                <div className="text-4xl font-bold text-purple-600">
                  {stats.winRate}%
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 페어니스 포인트 변화 그래프 (간단한 막대) */}
        <div className="card-azocsii p-4 mb-6">
          <h3 className="text-base font-bold text-primary mb-3">📈 페어니스 포인트 변화</h3>
          
          {entries.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-2">📊</div>
              <p className="text-sm text-gray-600">아직 추첨 기록이 없습니다</p>
            </div>
          ) : (
            <>
              <div className="flex items-end space-x-1 h-32">
                {entries.slice(0, 20).reverse().map((entry, index) => {
                  // 0P일 때는 30%, 나머지는 비율 계산 (최소 30%)
                  const baseHeight = (entry.fairnessPointsUsed / 50) * 100;
                  const height = entry.fairnessPointsUsed === 0 ? 30 : Math.max(baseHeight, 30);
                  const isWon = entry.status === 'WON';
                  const isLost = entry.status === 'LOST';
                  const isPending = entry.status === 'PENDING';

                  return (
                    <div key={entry.id} className="flex-1 flex flex-col items-center">
                      <div
                        className={`w-full rounded-t border ${
                          isWon 
                            ? 'bg-green-500 border-green-600' 
                            : isLost 
                            ? 'bg-red-500 border-red-600' 
                            : 'bg-yellow-500 border-yellow-600'
                        }`}
                        style={{ height: `${height}%` }}
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        {entry.fairnessPointsUsed}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between items-center mt-2 px-2">
                <div className="text-xs">
                  <span className="text-red-500 font-bold">← 과거</span>
                </div>
                <div className="text-xs text-gray-400">
                  최근 {Math.min(entries.length, 20)}회
                </div>
                <div className="text-xs">
                  <span className="text-green-500 font-bold">최신 →</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* 월별 기록 */}
        <div className="card-azocsii p-4">
          <h3 className="text-base font-bold text-primary mb-3">📅 월별 기록</h3>
          
          {Object.keys(byMonth).length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎲</div>
              <p className="text-gray-600">추첨 기록이 없습니다</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.keys(byMonth)
                .sort()
                .reverse()
                .map(month => {
                  const monthEntries = byMonth[month];
                  const monthWon = monthEntries.filter(e => e.status === 'WON').length;
                  const monthLost = monthEntries.filter(e => e.status === 'LOST').length;
                  const monthPending = monthEntries.filter(e => e.status === 'PENDING').length;

                  return (
                    <div key={month} className="border-2 border-gray-200 rounded-lg p-3">
                      {/* 월 헤더 */}
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-gray-800">
                          {month} ({monthEntries.length}회)
                        </h4>
                        <div className="flex gap-2 text-xs">
                          <span className="text-green-600">🎉 {monthWon}</span>
                          <span className="text-red-600">😢 {monthLost}</span>
                          <span className="text-yellow-600">⏳ {monthPending}</span>
                        </div>
                      </div>

                      {/* 상세 기록 */}
                      <div className="space-y-1">
                        {monthEntries.map(entry => (
                          <div
                            key={entry.id}
                            className={`flex items-center justify-between p-2 rounded ${
                              entry.status === 'WON'
                                ? 'bg-green-50'
                                : entry.status === 'LOST'
                                ? 'bg-red-50'
                                : 'bg-yellow-50'
                            }`}
                          >
                            <div className="flex items-center space-x-2">
                              <span className="text-lg">
                                {entry.status === 'WON' ? '🎉' : entry.status === 'LOST' ? '😢' : '⏳'}
                              </span>
                              <div>
                                <div className="text-sm font-medium">
                                  {format(new Date(entry.date), 'MM/dd (EEE)', { locale: ko })}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {format(new Date(entry.createdAt), 'HH:mm')} 신청
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className={`text-sm font-bold ${
                                entry.status === 'WON' ? 'text-green-600' :
                                entry.status === 'LOST' ? 'text-red-600' : 'text-yellow-600'
                              }`}>
                                {entry.status === 'WON' ? '당첨' : entry.status === 'LOST' ? '탈락' : '대기 중'}
                              </div>
                              <div className="text-xs text-gray-500">
                                {entry.fairnessPointsUsed}P 사용
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default LottoHistoryPage;
